import React from 'react';
import { useTranslation } from 'react-i18next';
import { CatalogAttributeApi, CatalogAttributeDefinition, CatalogAttributeFieldType } from '../../catalog/catalogAttributes';
import styles from '../../catalog/Catalog.module.css';

const fieldTypes: CatalogAttributeFieldType[] = ['text', 'number', 'date', 'select', 'yesNo'];

const emptyForm = {
  name: '',
  key: '',
  fieldType: 'text' as CatalogAttributeFieldType,
  isRequired: false,
  options: '',
  sortOrder: '0',
};

const parseOptions = (value: string) =>
  value.split(',').map(item => item.trim()).filter(Boolean);

type CategoryAttributeEditorProps = {
  categoryId: string;
  canUpdate: boolean;
  ns: 'products' | 'services';
  api: CatalogAttributeApi;
};

export const CategoryAttributeEditor: React.FC<CategoryAttributeEditorProps> = ({
  categoryId,
  canUpdate,
  ns,
  api,
}) => {
  const { t } = useTranslation([ns, 'common']);
  const [definitions, setDefinitions] = React.useState<CatalogAttributeDefinition[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState(emptyForm);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setDefinitions(await api.getAttributes(categoryId, true));
      setError(null);
    } catch {
      setError(t('failedToLoadAttributes', { ns }));
    } finally {
      setLoading(false);
    }
  }, [api, categoryId, ns, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const inherited = definitions.filter(item => item.inherited);
  const own = definitions.filter(item => !item.inherited);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const startEdit = (definition: CatalogAttributeDefinition) => {
    setEditingId(definition.id);
    setForm({
      name: definition.name,
      key: definition.key,
      fieldType: definition.fieldType,
      isRequired: definition.isRequired,
      options: definition.options.join(', '),
      sortOrder: String(definition.sortOrder ?? 0),
    });
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) return;
    if (form.fieldType === 'select' && parseOptions(form.options).length === 0) {
      setError(t('optionsRequired', { ns }));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        key: form.key.trim() || undefined,
        fieldType: form.fieldType,
        isRequired: form.isRequired,
        options: form.fieldType === 'select' ? parseOptions(form.options) : undefined,
        sortOrder: form.sortOrder.trim() ? Number(form.sortOrder) : 0,
      };
      if (editingId) {
        await api.updateAttribute(categoryId, editingId, {
          ...payload,
          clearOptions: form.fieldType !== 'select',
        });
      } else {
        await api.createAttribute(categoryId, payload);
      }
      startCreate();
      await load();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : t('failedToSaveAttribute', { ns }));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (attributeId: string) => {
    setSaving(true);
    setError(null);
    try {
      await api.deleteAttribute(categoryId, attributeId);
      if (editingId === attributeId) startCreate();
      await load();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : t('failedToDeleteAttribute', { ns }));
    } finally {
      setSaving(false);
    }
  };

  const fieldTypeLabel = (fieldType: CatalogAttributeFieldType) => {
    const keys: Record<CatalogAttributeFieldType, string> = {
      text: 'fieldTypeText',
      number: 'fieldTypeNumber',
      date: 'fieldTypeDate',
      select: 'fieldTypeSelect',
      yesNo: 'fieldTypeYesNo',
    };
    return t(keys[fieldType], { ns });
  };

  if (loading) {
    return <div className={styles.fieldHint}>{t('loadingAttributes', { ns })}</div>;
  }

  return (
    <div className={styles.attributePanel}>
      <div className={styles.sectionTitle}>{t('categoryFields', { ns })}</div>
      <p className={styles.fieldHint}>{t('categoryFieldsIntro', { ns })}</p>
      {error ? <div className={styles.fieldError}>{error}</div> : null}

      {inherited.length > 0 ? (
        <div>
          <div className={styles.detailLabel}>{t('inheritedFields', { ns })}</div>
          {inherited.map(item => (
            <div key={item.id} className={styles.attributeRow}>
              <div>
                <div className={styles.primaryText}>{item.name}</div>
                <div className={styles.secondaryText}>
                  {fieldTypeLabel(item.fieldType)}
                  {item.isRequired ? ` · ${t('fieldRequired', { ns })}` : ''}
                  {item.sourceCategoryName
                    ? ` · ${t('inheritedFrom', { ns, name: item.sourceCategoryName })}`
                    : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div>
        <div className={styles.detailLabel}>{t('ownFields', { ns })}</div>
        {own.length === 0 ? (
          <div className={styles.fieldHint}>{t('emptyFields', { ns })}</div>
        ) : own.map(item => (
          <div key={item.id} className={styles.attributeRow}>
            <div>
              <div className={styles.primaryText}>{item.name}</div>
              <div className={styles.secondaryText}>
                {fieldTypeLabel(item.fieldType)}
                {item.isRequired ? ` · ${t('fieldRequired', { ns })}` : ''}
                {item.fieldType === 'select' && item.options.length > 0 ? ` · ${item.options.join(', ')}` : ''}
              </div>
            </div>
            {canUpdate ? (
              <div className={styles.categoryActions}>
                <button type="button" className="btn btn-secondary" onClick={() => startEdit(item)} disabled={saving}>
                  {t('edit', { ns: 'common' })}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => void onDelete(item.id)} disabled={saving}>
                  {t('deleteField', { ns })}
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {canUpdate ? (
        <form className={styles.form} onSubmit={onSubmit}>
          <div className={`${styles.field} ${styles.fieldFull}`}>
            <div className={styles.sectionTitle}>
              {editingId ? t('editField', { ns }) : t('addField', { ns })}
            </div>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('fieldName', { ns })}</label>
            <input
              className={styles.input}
              value={form.name}
              placeholder={t('fieldNamePlaceholder', { ns })}
              onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('fieldType', { ns })}</label>
            <select
              className={styles.input}
              value={form.fieldType}
              onChange={event => setForm(prev => ({ ...prev, fieldType: event.target.value as CatalogAttributeFieldType }))}
            >
              {fieldTypes.map(item => (
                <option key={item} value={item}>{fieldTypeLabel(item)}</option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('fieldKey', { ns })}</label>
            <input
              className={styles.input}
              value={form.key}
              onChange={event => setForm(prev => ({ ...prev, key: event.target.value }))}
            />
            <div className={styles.fieldHint}>{t('fieldKeyHint', { ns })}</div>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t('sortOrder', { ns })}</label>
            <input
              className={styles.input}
              type="number"
              min="0"
              step="1"
              value={form.sortOrder}
              onChange={event => setForm(prev => ({ ...prev, sortOrder: event.target.value }))}
            />
          </div>
          {form.fieldType === 'select' ? (
            <div className={`${styles.field} ${styles.fieldFull}`}>
              <label className={styles.label}>{t('fieldOptions', { ns })}</label>
              <input
                className={styles.input}
                value={form.options}
                placeholder={t('fieldOptionsPlaceholder', { ns })}
                onChange={event => setForm(prev => ({ ...prev, options: event.target.value }))}
              />
              <div className={styles.fieldHint}>{t('fieldOptionsHint', { ns })}</div>
            </div>
          ) : null}
          <label className={`${styles.checkboxRow} ${styles.fieldFull}`}>
            <input
              type="checkbox"
              checked={form.isRequired}
              onChange={event => setForm(prev => ({ ...prev, isRequired: event.target.checked }))}
            />
            {t('fieldRequired', { ns })}
          </label>
          <div className={styles.actions}>
            {editingId ? (
              <button type="button" className="btn btn-secondary" onClick={startCreate} disabled={saving}>
                {t('cancel', { ns: 'common' })}
              </button>
            ) : null}
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {editingId ? t('saveField', { ns }) : t('addField', { ns })}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
};
